import { Group, Modal, Stack, Text, ThemeIcon, VisuallyHidden } from "@mantine/core";
import { useId } from "@mantine/hooks";
import type { CSSProperties, FormEventHandler, ReactNode } from "react";

import type { StudioSize } from "../../rules/sizing.js";

/**
 * The chrome every dialog in this package wears, carried as props rather than read from a
 * theme.
 *
 * Mountaya's own application registers the same values on its Mantine theme, which works
 * because every dialog in it renders under one theme. A published component has no such
 * guarantee: a consumer mounts it under THEIR provider, so a shell that expected a theme entry
 * would render an uncentred, square, unblurred dialog for everyone who did not know to add one,
 * and would look correct in this repository the whole time.
 *
 * `ModalRoot` needs them separately from `Modal`: the composed API reads its own
 * `useProps("ModalRoot", …)` key and never sees the shorthand's defaults.
 */
const modalChrome = { centered: true, padding: "md", radius: "md", shadow: "xl" } as const;

/** The subtly blurred, 55%-dimmed scrim behind every dialog. */
const modalOverlay = { backgroundOpacity: 0.55, blur: 2 } as const;

/** AppModalProps are the dialog shell's title band, body, action row and Mantine pass-throughs. */
export interface AppModalProps {
  children: ReactNode;
  /**
   * The close button's accessible name: the localized "Close", on every dialog, so a reader
   * navigating by name meets one target wherever they are. Never a different dismissal's word:
   * a confirm whose X announces "Cancel" beside a footer Cancel is two indistinguishable
   * buttons.
   *
   * Required rather than resolved here, which is the same contract every other surface in this
   * package keeps: copy arrives from the host, through `useMountayaStrings` or through a prop.
   */
  closeLabel: string;
  /**
   * Whether a click on the overlay closes the dialog. A shown-once dialog sets false.
   *
   * @default true
   */
  closeOnClickOutside?: boolean | undefined;
  /**
   * Whether Escape closes the dialog. A shown-once dialog sets false.
   *
   * @default true
   */
  closeOnEscape?: boolean | undefined;
  /**
   * The line under the title: either a one-sentence description of the dialog, or the identity
   * of the single named resource it acts on. Required: a dialog that states only a verb leaves
   * the reader to infer what it will act on, and one that drops the line between phases moves
   * everything under it.
   *
   * Pass `null` to render without one, which a confirm does: its body IS that sentence, and
   * repeating it in the header would say the same thing twice. Required-with-an-explicit-null
   * rather than optional, so leaving it out is a compile error instead of a silent omission.
   */
  description: ReactNode | null;
  /** The action row pinned below the body, usually a {@link ModalFooter}. */
  footer?: ReactNode;
  /** The glyph for the leading icon accent, framed in a light ThemeIcon. */
  icon?: ReactNode;
  /**
   * The accent the header glyph is tinted with: a design-system colour name or a CSS colour.
   *
   * A `string` rather than a tone, and rather than Mantine's own `MantineColor`. This shell is
   * generic: it carries a product's own identity colour as readily as a caution, and a closed
   * set of three would make the first unexpressible, and Mantine's colour type would make the
   * second a design system's name.
   *
   * {@link ConfirmModal} is the surface that does take a closed set (see its `tone`), because
   * a confirmation has three meanings and no more.
   */
  iconColor?: string | undefined;
  /**
   * Keeps the body mounted while closed (Mantine's `keepMounted`), for a dialog whose body
   * holds state that must survive a reopen.
   */
  keepMounted?: boolean | undefined;
  onClose: () => void;
  /**
   * Renders the body AND the footer as one `<form>` with this submit handler, so a footer whose
   * confirm omits `onConfirm` submits it. Without this the footer is a sibling of the body and a
   * form-backed dialog would have to smuggle its action row into `children` to keep the submit
   * button inside the form.
   */
  onSubmit?: FormEventHandler<HTMLFormElement> | undefined;
  opened: boolean;
  /**
   * The dialog's width: "md" is the form width, and a confirm or simple dialog passes "sm".
   *
   * @default "md"
   */
  size?: StudioSize | (string & {}) | number | undefined;
  /**
   * The dialog heading, and its accessible name. A STRING, not a node: it renders inside the
   * `<h2>` the dialog's `aria-labelledby` points at, where a node would both nest flow content
   * in a heading and read out as part of the announced name. Anything richer belongs on
   * `description`, which is a sibling of the heading for exactly that reason.
   */
  title: string;
  /**
   * Whether the title is drawn for assistive technology alone: it names the dialog all the
   * same, and no eye meets it. For a dialog whose body opens on a heading of its own that says
   * the same, so the name is not shown twice. The header then holds the close button alone,
   * with no divider under it.
   *
   * @default false
   */
  titleHidden?: boolean | undefined;
  /**
   * Whether the header shows its close button. A shown-once reveal phase hides it.
   *
   * @default true
   */
  withCloseButton?: boolean | undefined;
  zIndex?: number | undefined;
}

/**
 * The header of a dialog whose title is drawn for assistive technology alone: it holds the close
 * button and nothing an eye reads, so the sheet's divider and its spacing would rule off an empty
 * band above the body's own heading.
 */
const unruledHeader: CSSProperties = {
  borderBottomStyle: "none",
  marginBottom: 0,
  paddingBottom: 0,
};

/**
 * AppModal is the dialog shell every standard modal composes: a centred Mantine Modal whose
 * header pairs an optional icon accent with the title and a dimmed description beneath it, whose
 * body is a consistent stack, and whose footer is pinned below it.
 *
 * Routing every dialog through one shell is what makes a create form, a share dialog and a
 * confirm all settle the same way: the chrome lives here and a caller supplies only the body
 * and the action row.
 *
 * **Built on Mantine's COMPOSED parts rather than the `<Modal title={…}>` shorthand**, because
 * the shorthand renders whatever it is handed inside the `<h2>` that `aria-labelledby` points
 * at. A header carrying an icon and a description would then make both part of the dialog's
 * announced name ("Duplicate itinerary Alpine traverse 12 km 840 m") and nest flow content
 * inside a heading. Composed, the `<h2>` holds the title alone, the icon is its decorative
 * sibling, and the description becomes the `aria-describedby` target instead.
 *
 * **Fullscreen dialogs are deliberately not served here.** A panorama filling the viewport and a
 * wizard's header/main/footer column are flex shells that this stack-based body would fight.
 */
export const AppModal = ({
  children,
  closeLabel,
  closeOnClickOutside,
  closeOnEscape,
  description,
  footer,
  icon,
  iconColor,
  keepMounted,
  onClose,
  onSubmit,
  opened,
  size = "md",
  title,
  titleHidden = false,
  withCloseButton = true,
  zIndex,
}: AppModalProps) => {
  // One id drives both halves of the accessibility wiring. Mantine derives the heading's id as
  // `${id}-title` and points aria-describedby at `${id}-body`, the id it hands its own body
  // element; the description claims that second id below and the body moves to its own, so the
  // dialog is named by the title alone and described by the line under it. Mantine's useId (not
  // React's) because it yields a DOM- and CSS-safe token.
  const modalId = useId();

  // The body is composed once and wrapped in a <form> only when the dialog drives a submit, so
  // the two shapes cannot drift in gap or ordering. A wrapper rather than the Stack itself
  // because Mantine's Stack is not polymorphic: `component="form"` still types its submit
  // handler against a div.
  const body = (
    <Stack gap="md">
      {children}
      {footer}
    </Stack>
  );

  return (
    <Modal.Root
      {...modalChrome}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      id={modalId}
      keepMounted={keepMounted}
      onClose={onClose}
      opened={opened}
      size={size}
      zIndex={zIndex}
    >
      <Modal.Overlay {...modalOverlay} />
      <Modal.Content radius={modalChrome.radius}>
        <Modal.Header className="mtya-modal-header" style={titleHidden ? unruledHeader : undefined}>
          {/* One flex child, so the header band's space-between still pushes the close button to
              the far edge. The icon accent sits OUTSIDE the heading: it is decoration, and the
              <h2> is the dialog's accessible name. */}
          <Group align="flex-start" className="mtya-modal-heading" gap="sm" wrap="nowrap">
            {icon !== undefined ? (
              <ThemeIcon color={iconColor} radius="md" size="lg" variant="light">
                {icon}
              </ThemeIcon>
            ) : null}
            <Stack className="mtya-modal-heading-text" gap="xxs">
              {/* Text only. Anything else in here lands inside the heading and joins the
                  dialog's announced name. */}
              {titleHidden ? (
                <VisuallyHidden>
                  <Modal.Title className="mtya-modal-title">{title}</Modal.Title>
                </VisuallyHidden>
              ) : (
                <Modal.Title className="mtya-modal-title">{title}</Modal.Title>
              )}
              {/* component="div" because a description may be a composed row (a resource's
                  glyph, name and measurements), and a <p> cannot legally hold one. */}
              {description !== null ? (
                <Text
                  c="dimmed"
                  className="mtya-modal-description"
                  component="div"
                  id={`${modalId}-body`}
                  size="sm"
                >
                  {description}
                </Text>
              ) : null}
            </Stack>
          </Group>

          {withCloseButton ? <Modal.CloseButton aria-label={closeLabel} size="md" /> : null}
        </Modal.Header>

        {/* The description claimed Mantine's `-body` id above (that is what aria-describedby
            points at), so the body itself takes a distinct one. Setting aria-describedby on
            Modal.Content instead would type-check and do nothing: ModalBaseContent spreads
            incoming props BEFORE assigning the attribute, so the dialog's own value wins. */}
        <Modal.Body id={description !== null ? `${modalId}-content` : undefined}>
          {onSubmit ? <form onSubmit={onSubmit}>{body}</form> : body}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
